import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  BarChart3, 
  TrendingUp, 
  Calendar, 
  Award, 
  Plus, 
  Activity,
  Target,
  Heart,
  Brain,
  Sparkles,
  Star,
  Zap,
  Clock,
  Users,
  Trophy,
  TrendingDown,
  Minus,
  CheckCircle,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Eye,
  AlertTriangle,
  Lightbulb,
  RefreshCw
} from 'lucide-react';
import api from '../utils/api';
import TrialCountdown from '../components/TrialCountdown';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// Custom CSS for range sliders
const sliderStyles = `
  .slider::-webkit-slider-thumb {
    appearance: none;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ec4899, #8b5cf6);
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
  }
  
  .slider::-webkit-slider-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
  }
  
  .slider::-moz-range-thumb {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ec4899, #8b5cf6);
    cursor: pointer;
    border: none;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
  }
  
  .slider::-moz-range-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
  }
  
  .slider::-webkit-slider-track {
    background: linear-gradient(90deg, #e5e7eb 0%, #d1d5db 100%);
    border-radius: 10px;
    height: 8px;
  }
  
  .slider::-moz-range-track {
    background: linear-gradient(90deg, #e5e7eb 0%, #d1d5db 100%);
    border-radius: 10px;
    height: 8px;
    border: none;
  }
`;

const Dashboard = () => {
  const { user } = useAuth();
  const [moodData, setMoodData] = useState([]);
  const [recentMoods, setRecentMoods] = useState([]);
  const [sessionStats, setSessionStats] = useState({});
  const [insights, setInsights] = useState({});
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsFromCache, setInsightsFromCache] = useState(false);
  const [insightsLastUpdated, setInsightsLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMoodModal, setShowMoodModal] = useState(false);
  const [newMood, setNewMood] = useState({ mood: 5, anxiety: 5, stress: 5 });



  useEffect(() => {
    if (user?._id) {
      fetchProgressData();
    }
  }, [user?._id]);

  const fetchProgressData = async () => {
    try {
      setLoading(true);

      console.log('🔍 Fetching progress data for user:', user?._id);

      // Fetch progress summary
      const summaryRes = await api.get('/progress/summary?period=30');
      const summary = summaryRes.data.summary;

      // Fetch mood tracking data
      const moodRes = await api.get('/progress/mood-tracking?days=7');
      const moodTrackingData = moodRes.data.moodData;

      // Fetch recent mood entries
      const recentMoodsRes = await api.get('/progress/recent-moods');
      const recentMoodsData = recentMoodsRes.data.recentMoods;

      // Fetch session statistics from Chat model
      const sessionStatsRes = await api.get('/progress/session-stats');
      const sessionStatsData = sessionStatsRes.data.sessionStats;

      // Fetch insights
      const insightsRes = await api.get('/progress/insights?period=30');
      const userInsights = insightsRes.data.insights;
      const fromCache = insightsRes.data.fromCache || false;
      const lastUpdated = insightsRes.data.lastUpdated ? new Date(insightsRes.data.lastUpdated) : new Date();

      // Process mood data for charts
      const processedMoodData = moodTrackingData.map(d => ({
        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        mood: d.mood,
        anxiety: d.anxiety,
        stress: d.stress
      }));

      // Process session data for charts using actual daily session data
      const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const sessionChartData = weekDays.map(day => {
        // Map the day names to match the backend data
        const dayMapping = {
          'Mon': 'Monday',
          'Tue': 'Tuesday', 
          'Wed': 'Wednesday',
          'Thu': 'Thursday',
          'Fri': 'Friday',
          'Sat': 'Saturday',
          'Sun': 'Sunday'
        };
        return sessionStatsData.dailySessions[dayMapping[day]] || 0;
      });

      setMoodData(processedMoodData);
      setRecentMoods(recentMoodsData);
      setSessionStats({
        totalSessions: sessionStatsData.totalSessions,
        thisWeek: sessionStatsData.thisWeek,
        thisMonth: summary.totalSessions,
        averageSessionLength: sessionStatsData.averageSessionLength || 0,
        favoriteTherapist: sessionStatsData.favoriteTherapist ? `Dr. ${sessionStatsData.favoriteTherapist.charAt(0).toUpperCase() + sessionStatsData.favoriteTherapist.slice(1)}` : 'None',
        dailySessions: sessionStatsData.dailySessions || {}
      });

      setInsights(userInsights);
      setInsightsFromCache(fromCache);
      setInsightsLastUpdated(lastUpdated);

    } catch (error) {
      console.error('Failed to fetch progress data:', error);
      
      // Show user-friendly error message
      if (error.response?.status === 401) {
        alert('Please log in to view your progress data.');
      } else if (error.response?.status === 403) {
        alert('Please upgrade your subscription to access progress tracking.');
      } else {
        alert('Failed to load progress data. Please try refreshing the page.');
      }
      
      // Set empty arrays instead of mock data
      setMoodData([]);
      setRecentMoods([]);
      setSessionStats({
        totalSessions: 0,
        thisWeek: 0,
        thisMonth: 0,
        averageSessionLength: 0,
        favoriteTherapist: 'None',
        dailySessions: {}
      });

      setInsights({});
    } finally {
      setLoading(false);
    }
  };

  const refreshInsights = async () => {
    try {
      setInsightsLoading(true);
      console.log('🔄 Refreshing AI insights...');
      
      const response = await api.post('/progress/insights/refresh');
      const newInsights = response.data.insights;
      
      setInsights(newInsights);
      setInsightsFromCache(false);
      setInsightsLastUpdated(new Date());
      
      console.log('✅ AI insights refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh insights:', error);
      alert('Failed to refresh insights. Please try again.');
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleAddMood = async () => {
    try {
      const response = await api.post('/progress/mood', {
        therapistId: 'general',
        mood: newMood.mood,
        anxiety: newMood.anxiety,
        stress: newMood.stress
      });

      setShowMoodModal(false);
      setNewMood({ mood: 5, anxiety: 5, stress: 5 });
      
      // Refresh insights specifically since mood data has changed
      await refreshInsights();
      
      // Also refresh other progress data
      fetchProgressData();
    } catch (error) {
      console.error('Failed to add mood entry:', error);
      
      // Show user-friendly error message
      alert(`Failed to save mood entry: ${error.response?.data?.error || error.message}`);
    }
  };



  const moodChartData = {
    labels: moodData.map(d => d.date),
    datasets: [
      {
        label: 'Happiness Level',
        data: moodData.map(d => d.mood),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
      },
      {
        label: 'Calmness Level',
        data: moodData.map(d => d.anxiety),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgb(239, 68, 68)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
      },
      {
        label: 'Relaxation Level',
        data: moodData.map(d => d.stress),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgb(245, 158, 11)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
      },
    ],
  };

  // Calculate session chart data from sessionStats
  const sessionChartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Sessions This Week',
        data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => {
          // Map the day names to match the backend data
          const dayMapping = {
            'Mon': 'Monday',
            'Tue': 'Tuesday', 
            'Wed': 'Wednesday',
            'Thu': 'Thursday',
            'Fri': 'Friday',
            'Sat': 'Saturday',
            'Sun': 'Sunday'
          };
          return sessionStats.dailySessions?.[dayMapping[day]] || 0;
        }),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(6, 182, 212, 0.8)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)',
          'rgb(239, 68, 68)',
          'rgb(139, 92, 246)',
          'rgb(236, 72, 153)',
          'rgb(6, 182, 212)'
        ],
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: '600'
          },
          color: '#374151'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#111827',
        bodyColor: '#374151',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        cornerRadius: 12,
        displayColors: true,
        titleFont: {
          size: 14,
          weight: '600'
        },
        bodyFont: {
          size: 12
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 10,
        grid: {
          color: 'rgba(229, 231, 235, 0.5)',
          drawBorder: false
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 11,
            weight: '500'
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 11,
            weight: '500'
          }
        }
      }
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
        borderWidth: 2
      },
      line: {
        borderWidth: 3
      }
    }
  };

  const getMoodTrend = () => {
    return insights.moodTrend || 'stable';
  };

  const getMoodTrendColor = () => {
    const trend = getMoodTrend();
    switch (trend) {
      case 'improving': return 'text-green-600';
      case 'declining': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getMoodTrendIcon = () => {
    const trend = getMoodTrend();
    switch (trend) {
      case 'improving': return <ArrowUp className="w-5 h-5" />;
      case 'declining': return <ArrowDown className="w-5 h-5" />;
      default: return <ArrowRight className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-center py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Your Progress...</h3>
            <p className="text-gray-600">Gathering your therapy insights and statistics</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Custom CSS for sliders */}
      <style dangerouslySetInnerHTML={{ __html: sliderStyles }} />
      

      
      {/* Trial Countdown */}
      <TrialCountdown />
      
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-3xl mb-6 shadow-lg">
          <Target className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Progress Dashboard</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
          Track your therapy journey and celebrate your mental health progress with detailed insights and beautiful visualizations.
        </p>
        <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span>AI Insights</span>
          </div>
          <div className="flex items-center space-x-2">
            <Heart className="w-4 h-4 text-red-500" />
            <span>Mood Tracking</span>
          </div>
          <div className="flex items-center space-x-2">
            <Star className="w-4 h-4 text-yellow-500" />
            <span>Progress Analytics</span>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-center space-x-2 bg-white px-4 py-2 rounded-lg shadow-sm">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl shadow-lg">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs font-medium bg-white/80 backdrop-blur-sm border border-blue-200 text-blue-700 px-2 py-1 rounded-full">
              Total
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">Total Sessions</p>
            <p className="text-3xl font-bold text-gray-900">{sessionStats.totalSessions}</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl shadow-lg">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs font-medium bg-white/80 backdrop-blur-sm border border-green-200 text-green-700 px-2 py-1 rounded-full">
              This Week
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">This Week</p>
            <p className="text-3xl font-bold text-gray-900">{sessionStats.thisWeek}</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl shadow-lg">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs font-medium bg-white/80 backdrop-blur-sm border border-purple-200 text-purple-700 px-2 py-1 rounded-full">
              Average
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">Avg Session</p>
            <p className="text-3xl font-bold text-gray-900">{sessionStats.averageSessionLength}m</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className={`p-3 rounded-xl shadow-lg ${getMoodTrend() === 'improving' ? 'bg-gradient-to-br from-green-400 to-emerald-500' : getMoodTrend() === 'declining' ? 'bg-gradient-to-br from-red-400 to-pink-500' : 'bg-gradient-to-br from-gray-400 to-gray-500'}`}>
              <Activity className="w-6 h-6 text-white" />
            </div>
            <span className={`text-xs font-medium bg-white/80 backdrop-blur-sm border px-2 py-1 rounded-full ${getMoodTrend() === 'improving' ? 'border-green-200 text-green-700' : getMoodTrend() === 'declining' ? 'border-red-200 text-red-700' : 'border-gray-200 text-gray-700'}`}>
              {getMoodTrend()}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">Mood Trend</p>
            <div className="flex items-center space-x-2">
              <span className="text-2xl font-bold text-gray-900 capitalize">{getMoodTrend()}</span>
              <div className={`p-1 rounded-full ${getMoodTrend() === 'improving' ? 'bg-green-100 text-green-600' : getMoodTrend() === 'declining' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
                {getMoodTrendIcon()}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-xl shadow-lg">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs font-medium bg-white/80 backdrop-blur-sm border border-yellow-200 text-yellow-700 px-2 py-1 rounded-full">
              Favorite
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">Favorite Therapist</p>
            <p className="text-lg font-bold text-gray-900 truncate">{sessionStats.favoriteTherapist}</p>
          </div>
        </div>
      </div>

      {/* Recent Mood Entries */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-pink-400 to-purple-500 rounded-lg">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Recent Mood Entries</h3>
          </div>
          <button
            onClick={() => setShowMoodModal(true)}
            className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Mood</span>
            <Sparkles className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {recentMoods.length > 0 ? (
            recentMoods.map((entry, index) => (
              <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full"></div>
                    <span className="text-sm font-semibold text-gray-700">{entry.date}</span>
                  </div>
                  <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {new Date(entry.date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-700">Happiness:</span>
                    </div>
                    <span className="text-sm font-bold text-blue-600">{entry.mood}/10</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-700">Calmness:</span>
                    </div>
                    <span className="text-sm font-bold text-red-600">{entry.anxiety || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-700">Relaxation:</span>
                    </div>
                    <span className="text-sm font-bold text-orange-600">{entry.stress || 'N/A'}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-3 text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl mb-4">
                <Heart className="w-8 h-8 text-pink-500" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">No Mood Entries Yet</h4>
              <p className="text-gray-600 mb-4">Start tracking your emotional well-being to see your progress over time.</p>
              <button
                onClick={() => setShowMoodModal(true)}
                className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Add Your First Entry
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Mood Tracking Chart */}
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-xl border border-blue-100 p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Happiness & Wellness Tracking</h3>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            {moodData.length > 0 ? (
              <Line data={moodChartData} options={chartOptions} height={300} />
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl mb-4">
                    <TrendingUp className="w-8 h-8 text-blue-500" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">No Mood Data Yet</h4>
                  <p className="text-gray-600 mb-4">Start tracking your mood to see beautiful visualizations</p>
                  <button
                    onClick={() => setShowMoodModal(true)}
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    Add First Entry
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 flex items-center space-x-2 text-sm text-gray-600">
            {moodData.length > 0 && (
              <>
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span>Happiness</span>
                <div className="w-3 h-3 bg-red-500 rounded-full ml-4"></div>
                <span>Anxiety</span>
                <div className="w-3 h-3 bg-orange-500 rounded-full ml-4"></div>
                <span>Stress</span>
              </>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-3">
            Track your daily emotional well-being on a scale of 1-10
          </p>
        </div>

        {/* Session Activity Chart */}
        <div className="bg-gradient-to-br from-white to-green-50 rounded-3xl shadow-xl border border-green-100 p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Weekly Session Activity</h3>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            {sessionStats.thisWeek > 0 ? (
              <Bar data={sessionChartData} options={chartOptions} height={300} />
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl mb-4">
                    <Activity className="w-8 h-8 text-green-500" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">No Sessions This Week</h4>
                  <p className="text-gray-600 mb-4">Complete therapy sessions to see your activity patterns</p>
                  <button
                    onClick={() => window.location.href = '/chat'}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    Start Session
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 flex items-center justify-between">
            {sessionStats.thisWeek > 0 && (
              <>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span>Daily Sessions</span>
                </div>
                <div className="text-sm font-medium text-green-600">
                  Total: {sessionStats.thisWeek} this week
                </div>
              </>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-3">
            Number of therapy sessions completed each day this week
          </p>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-gradient-to-br from-white to-purple-50 rounded-3xl shadow-xl border border-purple-100 p-8">
        <div className="mb-6">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">AI Insights & Recommendations</h3>
          </div>
          
          {/* Status and Controls - Mobile Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
            {/* Left side - Tags */}
            <div className="flex flex-wrap items-center gap-2">
              {insights.aiGenerated && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <Sparkles className="w-3 h-3 mr-1" />
                  AI Generated
                </span>
              )}
              
              {/* Cache Status */}
              {insightsFromCache ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-1"></span>
                  Cached
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
                  Fresh
                </span>
              )}
            </div>
            
            {/* Right side - Update time and refresh button */}
            <div className="flex items-center space-x-3">
              {insightsLastUpdated && (
                <span className="text-xs text-gray-500">
                  Updated: {insightsLastUpdated.toLocaleTimeString()}
                </span>
              )}
              
              {/* Refresh Button */}
              <button
                onClick={refreshInsights}
                disabled={insightsLoading}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  insightsLoading 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-purple-100 text-purple-600 hover:bg-purple-200 hover:scale-105'
                }`}
                title="Refresh AI Insights"
              >
                <RefreshCw className={`w-4 h-4 ${insightsLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Mood Trend Analysis */}
        {insights.moodTrend && (
          <div className="mb-6">
            <div className="bg-white border-2 border-purple-100 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-white" />
                </div>
                <h4 className="font-bold text-gray-900">Mood Trend Analysis</h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  insights.moodTrend === 'improving' ? 'bg-green-100 text-green-800' :
                  insights.moodTrend === 'declining' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {insights.moodTrend.charAt(0).toUpperCase() + insights.moodTrend.slice(1)}
                </span>
              </div>
              {insights.moodTrendExplanation ? (
                <p className="text-gray-700 leading-relaxed">{insights.moodTrendExplanation}</p>
              ) : (
                <p className="text-gray-700 leading-relaxed">
                  Your mood has been {insights.moodTrend} over the last few entries. 
                  {insights.moodTrend === 'improving' && ' Keep up the great work!'}
                  {insights.moodTrend === 'declining' && ' Consider discussing this with your therapist.'}
                  {insights.moodTrend === 'stable' && ' Consistency is good, but consider exploring new strategies if you want to improve further.'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Recommendations */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {insights.recommendations && insights.recommendations.length > 0 ? (
            insights.recommendations.map((recommendation, index) => (
              <div key={index} className="bg-white border-2 border-purple-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-bold text-gray-900">Recommendation {index + 1}</h4>
                </div>
                <p className="text-gray-700 leading-relaxed">{recommendation}</p>
              </div>
            ))
          ) : (
            <>
              <div className="bg-white border-2 border-green-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-bold text-gray-900">Positive Trend</h4>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Your mood has been consistently improving over the past week. 
                  Consider continuing with your current therapy approach.
                </p>
              </div>
              
              <div className="bg-white border-2 border-yellow-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-lg">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-bold text-gray-900">Timing Suggestion</h4>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Try scheduling sessions earlier in the day when your energy levels are higher. 
                  This might improve your therapy outcomes.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Behavioral Patterns */}
        {insights.patterns && insights.patterns.length > 0 && (
          <div className="mb-6">
            <div className="bg-white border-2 border-blue-100 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg">
                  <Eye className="w-4 h-4 text-white" />
                </div>
                <h4 className="font-bold text-gray-900">Behavioral Patterns</h4>
              </div>
              <div className="space-y-3">
                {insights.patterns.map((pattern, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700 leading-relaxed">{pattern}</p>
                  </div>
                ))}
              </div>
              {insights.patternsExplanation && (
                <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-800 leading-relaxed">{insights.patternsExplanation}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Risk Factors */}
        {insights.riskFactors && insights.riskFactors.length > 0 && (
          <div className="mb-6">
            <div className="bg-white border-2 border-red-100 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-gradient-to-br from-red-400 to-pink-500 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-white" />
                </div>
                <h4 className="font-bold text-gray-900">Risk Factors</h4>
              </div>
              <div className="space-y-3">
                {insights.riskFactors.map((risk, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700 leading-relaxed">{risk}</p>
                  </div>
                ))}
              </div>
              {insights.riskFactorsExplanation && (
                <div className="mt-4 p-4 bg-red-50 rounded-xl">
                  <p className="text-sm text-red-800 leading-relaxed">{insights.riskFactorsExplanation}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recommendations Explanation */}
        {insights.recommendationsExplanation && (
          <div className="bg-white border-2 border-green-100 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg">
                <Lightbulb className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-bold text-gray-900">Why These Recommendations?</h4>
            </div>
            <p className="text-gray-700 leading-relaxed">{insights.recommendationsExplanation}</p>
          </div>
        )}
      </div>

      {/* Mood Entry Modal */}
      {showMoodModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl border border-gray-100 p-8 w-full max-w-md transform transition-all duration-300">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-gradient-to-br from-pink-400 to-purple-500 rounded-lg">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Add Mood Entry</h3>
            </div>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <label className="block text-sm font-semibold text-gray-700 mb-3">Happiness Level (1-10)</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={newMood.mood}
                  onChange={(e) => setNewMood({ ...newMood, mood: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>😢 Very Sad</span>
                  <span className="text-lg font-bold text-blue-600">{newMood.mood}/10</span>
                  <span>😊 Very Happy</span>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <label className="block text-sm font-semibold text-gray-700 mb-3">Calmness Level (1-10)</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={newMood.anxiety}
                  onChange={(e) => setNewMood({ ...newMood, anxiety: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>😰 Very Anxious</span>
                  <span className="text-lg font-bold text-red-600">{newMood.anxiety}/10</span>
                  <span>😌 Very Calm</span>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <label className="block text-sm font-semibold text-gray-700 mb-3">Relaxation Level (1-10)</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={newMood.stress}
                  onChange={(e) => setNewMood({ ...newMood, stress: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>😤 Very Stressed</span>
                  <span className="text-lg font-bold text-orange-600">{newMood.stress}/10</span>
                  <span>😌 Very Relaxed</span>
                </div>
              </div>
            </div>
            <div className="flex space-x-4 mt-8">
              <button
                onClick={() => setShowMoodModal(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMood}
                className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Save Entry</span>
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default Dashboard; 